import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../api/firebase";
import { useAuth } from "../helpers/Auth";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import { isPremiumFromPreferences } from "../helpers/premiumHelpers";

export type PremiumStatus = "loading" | "premium" | "not_premium" | "error";

export interface PremiumState {
  isPremium: boolean;
  status: PremiumStatus;
  expiresAt: string | null;
  refreshPremiumStatus: () => void;
}

const defaultState: PremiumState = {
  isPremium: false,
  status: "loading",
  expiresAt: null,
  refreshPremiumStatus: () => {},
};

const PremiumContext = createContext<PremiumState>(defaultState);

const REFRESH_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutes

interface CheckPremiumResponse {
  isPremium: boolean;
  expiresAt: string | null;
}

export const PremiumProvider = ({ children }: { children: React.ReactNode }) => {
  const { authUID } = useAuth();
  const preferences = useFirebasePreferences();
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const [state, setState] = useState<Omit<PremiumState, "refreshPremiumStatus">>({
    isPremium: false,
    status: "loading",
    expiresAt: null,
  });
  const lastRefreshRef = useRef<number>(0);

  const checkPremium = useCallback(async () => {
    if (!authUID) {
      setState({ isPremium: false, status: "not_premium", expiresAt: null });
      return;
    }

    setState((prev) => ({ ...prev, status: "loading" }));

    try {
      const checkPremiumStatus = httpsCallable<void, CheckPremiumResponse>(
        functions,
        "checkPremiumStatus",
      );
      const result = await checkPremiumStatus();
      const { isPremium: rcPremium, expiresAt } = result.data;
      // Re-read preferences after the await — they may have loaded while the
      // cloud function was in flight, and we don't want a stale `false` here
      // to overwrite eternal_subscriber=true.
      const hasEternalSubscriber = isPremiumFromPreferences(preferencesRef.current);
      const isPremium = rcPremium || hasEternalSubscriber;

      setState({
        isPremium,
        status: isPremium ? "premium" : "not_premium",
        expiresAt,
      });
      lastRefreshRef.current = Date.now();
    } catch {
      if (isPremiumFromPreferences(preferencesRef.current)) {
        setState({ isPremium: true, status: "premium", expiresAt: null });
      } else {
        setState({ isPremium: false, status: "error", expiresAt: null });
      }
    }
  }, [authUID]);

  // Check premium status when auth changes
  useEffect(() => {
    checkPremium();
  }, [authUID]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect eternal_subscriber preference changes without re-calling RevenueCat
  useEffect(() => {
    if (isPremiumFromPreferences(preferences)) {
      setState((prev) =>
        prev.isPremium ? prev : { isPremium: true, status: "premium", expiresAt: prev.expiresAt }
      );
    }
  }, [preferences]);

  const refreshPremiumStatus = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshRef.current >= REFRESH_DEBOUNCE_MS) {
      checkPremium();
    }
  }, [checkPremium]);

  // Refresh on visibility change (tab refocus), debounced
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPremiumStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPremiumStatus]);

  return (
    <PremiumContext.Provider
      value={{ ...state, refreshPremiumStatus }}
    >
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremiumStatus = (): PremiumState => {
  return useContext(PremiumContext);
};
